import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useShop } from '@/features/shops/hooks/use-shop';
import { useDeleteShop } from '@/features/shops/hooks/use-delete-shop';
import { useUpdateShopStatus, useUpdateShopDelivery } from '@/features/shops/hooks/use-update-shop';
import { DeleteShopDialog } from '@/features/shops/components/DeleteShopDialog';
import { useCountriesMap } from '@/lib/hooks/use-countries';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/atoms/card';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { Skeleton } from '@/components/atoms/skeleton';
import { Separator } from '@/components/atoms/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/atoms/avatar';
import { Switch } from '@/components/atoms/switch';
import { toast } from 'sonner';
import {
  ArrowLeft,
  MapPin,
  Package,
  ShoppingCart,
  Edit,
  Phone,
  Mail,
  Star,
  Globe,
  Clock,
  Eye,
  Users,
  Truck,
  Store,
  User,
  Calendar,
  Tag,
  Percent,
  XCircle,
  Pause,
  Play,
  Loader2,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatDate, formatPrice, getAmountFromPriceObject, convertFromEur } from '@/lib/utils';
import { useConfigStore } from '@/lib/stores/config.store';
import type { ShopAddress, CountryObject, ShopDayHours, ShopShippingRate, ShopPromotionSummary } from '@/lib/types';

function isAddressObject(address: ShopAddress | string | undefined): address is ShopAddress {
  return typeof address === 'object' && address !== null;
}

function isCountryObject(country: string | CountryObject | undefined): country is CountryObject {
  return typeof country === 'object' && country !== null && 'name' in country;
}

function getCountryName(country: string | CountryObject | undefined): string | null {
  if (!country) return null;
  if (typeof country === 'string') return country;
  return country.name || null;
}

function formatAddress(address: ShopAddress | string | undefined): string | null {
  if (!address) return null;
  if (typeof address === 'string') return address;

  const countryName = getCountryName(address.country);
  const parts = [
    address.address_line1,
    address.address_line2,
    address.landmark,
    address.city,
    address.postal_code,
    countryName,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(', ') : null;
}

function formatTime(time: string | undefined): string {
  if (!time) return '--:--';
  // Handle ISO date strings (2025-04-13T06:00:41.957Z)
  if (time.includes('T')) {
    try {
      const date = new Date(time);
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return time;
    }
  }
  return time;
}

interface DayScheduleProps {
  day: string;
  hours?: ShopDayHours;
  t: (key: string) => string;
}

function DaySchedule({ day, hours, t }: DayScheduleProps) {
  if (!hours) return null;

  return (
    <div className="flex items-center justify-between py-2 border-b last:border-0">
      <span className="font-medium capitalize">{t(`shops.days.${day}`)}</span>
      {hours.enable ? (
        <div className="text-sm text-right">
          {hours.startDateMorning && hours.closeDateMorning && (
            <div>
              {formatTime(hours.startDateMorning)} - {formatTime(hours.closeDateMorning)}
            </div>
          )}
          {hours.startingDateAfternoon && hours.closeDateAfternoon && (
            <div className="text-muted-foreground">
              {formatTime(hours.startingDateAfternoon)} - {formatTime(hours.closeDateAfternoon)}
            </div>
          )}
        </div>
      ) : (
        <Badge variant="secondary">{t('shops.closed')}</Badge>
      )}
    </div>
  );
}

export default function ShopDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: shop, isLoading, error, isFetching } = useShop(id ?? '');
  const deleteShop = useDeleteShop();
  const updateShopStatus = useUpdateShopStatus();
  const updateShopDelivery = useUpdateShopDelivery();
  const { getCountryFlag: getCountryFlagByCode, getCountryName: getCountryNameByCode } = useCountriesMap();
  const { currency } = useConfigStore();

  const handleDelete = ({ reason, targetShopRefId }: { reason?: string; targetShopRefId?: string }) => {
    if (!shop) return;
    deleteShop.mutate(
      {
        id: shop.refId || shop.id,
        reason,
        targetShopRefId,
      },
      {
        onSuccess: () => {
          toast.success(t('shops.deleteSuccess'));
          navigate('/shops');
        },
        onError: () => {
          toast.error(t('shops.deleteError'));
        },
      }
    );
  };

  // Action rapide : activer/désactiver le retrait en magasin (préserve la livraison à domicile).
  const handleTogglePickup = (enable: boolean) => {
    if (!shop) return;
    const current = (shop.delivery as 'store' | 'home' | 'both') ?? 'both';
    const newDelivery: 'store' | 'home' | 'both' = enable
      ? current === 'home'
        ? 'both'
        : current
      : 'home';
    updateShopDelivery.mutate(
      { id: shop.refId || shop.id, delivery: newDelivery },
      {
        onSuccess: () => {
          toast.success(
            enable
              ? t('shops.pickupEnabled', 'Retrait en magasin activé')
              : t('shops.pickupDisabled', 'Retrait en magasin désactivé')
          );
        },
        onError: () => toast.error(t('shops.deliveryUpdateError', 'Erreur lors de la mise à jour')),
      }
    );
  };

  const handleToggleStatus = () => {
    if (!shop) return;
    const isActive = shop.status === 'online' || shop.isActive;
    const newStatus = isActive ? 'offline' : 'online';
    updateShopStatus.mutate(
      { id: shop.refId || shop.id, status: newStatus },
      {
        onSuccess: () => {
          toast.success(
            newStatus === 'online'
              ? t('shops.activateSuccess', 'Magasin activé avec succès')
              : t('shops.deactivateSuccess', 'Magasin désactivé avec succès')
          );
        },
        onError: () => {
          toast.error(t('shops.statusUpdateError', 'Erreur lors de la mise à jour du statut'));
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-9 w-9" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Card>
          <CardContent className="pt-6">
            <Skeleton className="h-32 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !shop) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-destructive">{t('shops.error')}</p>
      </div>
    );
  }

  const shopRefId = shop.refId || shop.id;
  const hasOpenHours = shop.monday || shop.tuesday || shop.wednesday || shop.thursday || shop.friday || shop.saturday || shop.sunday;

  // Get status badge variant
  const getStatusVariant = () => {
    if (shop.status === 'suspended') return 'destructive';
    if (shop.status === 'offline' || !shop.isActive) return 'secondary';
    return 'default';
  };

  const getStatusText = () => {
    if (shop.status === 'suspended') return t('shops.suspended');
    if (shop.status === 'offline') return t('shops.offline');
    if (!shop.isActive) return t('shops.inactive');
    return t('shops.active');
  };

  return (
    <div className="space-y-6 w-full overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/shops" className="shrink-0">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="size-5" />
            </Button>
          </Link>
          <Avatar className="h-10 w-10 sm:h-12 sm:w-12 shrink-0">
            <AvatarImage src={shop.logo} />
            <AvatarFallback>{shop.name[0]}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold truncate">{shop.name}</h1>
            <div className="flex items-center gap-2 flex-wrap text-sm">
              {shop.city && (
                <span className="text-muted-foreground flex items-center gap-1">
                  <MapPin className="size-3" />
                  {shop.city}
                </span>
              )}
              {shop.manager && (
                <span className="text-muted-foreground flex items-center gap-1">
                  <User className="size-3" />
                  {shop.manager}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2 border rounded-lg px-2 py-1">
            {(updateShopStatus.isPending || isFetching) && (
              <Loader2 className="size-4 animate-spin text-muted-foreground" />
            )}
            <Switch
              checked={shop.status === 'online' || shop.isActive}
              onCheckedChange={handleToggleStatus}
              disabled={updateShopStatus.isPending || isFetching || shop.status === 'suspended'}
            />
          </div>
          <Badge variant={getStatusVariant()}>
            {getStatusText()}
          </Badge>
          {shop.delivery && (
            <Badge variant="outline" className="gap-1">
              <Truck className="size-3" />
              {t(`shops.deliveryTypes.${shop.delivery}`)}
            </Badge>
          )}
          <div className="flex items-center gap-2 border rounded-lg px-2 py-1">
            {updateShopDelivery.isPending && (
              <Loader2 className="size-4 animate-spin text-muted-foreground" />
            )}
            <span className="text-sm text-muted-foreground">
              {t('shops.pickupToggleLabel', 'Retrait en magasin')}
            </span>
            <Switch
              checked={shop.delivery !== 'home'}
              onCheckedChange={handleTogglePickup}
              disabled={updateShopDelivery.isPending || isFetching}
            />
          </div>
          <Link to={`/shops/${shopRefId}/edit`}>
            <Button variant="outline" size="sm">
              <Edit className="size-4" />
            </Button>
          </Link>
          <DeleteShopDialog
            shop={shop}
            onConfirm={handleDelete}
            isLoading={deleteShop.isPending}
            variant="icon"
          />
        </div>
      </div>

      {/* Banner */}
      {shop.banner && (
        <div className="h-48 rounded-lg overflow-hidden">
          <img
            src={shop.banner}
            alt={shop.name}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <div className="grid gap-4 md:gap-6 md:grid-cols-2 lg:grid-cols-3 w-full">
        {/* Statistics */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Store className="size-5" />
              {t('shops.stats')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Package className="size-4 text-muted-foreground" />
                <span>{t('shops.products')}</span>
              </div>
              <span className="font-bold">{shop.productCount || shop.totalProducts || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShoppingCart className="size-4 text-muted-foreground" />
                <span>{t('shops.orders')}</span>
              </div>
              <span className="font-bold">{shop.orderCount || shop.totalOrders || 0}</span>
            </div>
            {shop.subscriberCount !== undefined && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="size-4 text-muted-foreground" />
                  <span>{t('shops.subscribers')}</span>
                </div>
                <span className="font-bold">{shop.subscriberCount}</span>
              </div>
            )}
            {(shop.rating !== undefined || shop.reviews) && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Star className="size-4 text-muted-foreground" />
                  <span>{t('shops.rating')}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Star className="size-4 fill-yellow-400 text-yellow-400" />
                  <span className="font-bold">
                    {(() => {
                      const rating = shop.reviews?.averageRating ?? shop.rating ?? 0;
                      return typeof rating === 'number' ? rating.toFixed(1) : '0.0';
                    })()}
                  </span>
                  {(shop.reviews?.totalReviews || shop.reviewCount) && (
                    <span className="text-xs text-muted-foreground">
                      ({shop.reviews?.totalReviews || shop.reviewCount})
                    </span>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Metrics */}
        {shop.metrics && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="size-5" />
                {t('shops.metrics')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{t('shops.totalViews')}</span>
                <span className="font-bold">{shop.metrics.views}</span>
              </div>
              <Separator />
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-lg font-bold">{shop.metrics.dailyViews}</p>
                  <p className="text-xs text-muted-foreground">{t('shops.daily')}</p>
                </div>
                <div>
                  <p className="text-lg font-bold">{shop.metrics.weeklyViews}</p>
                  <p className="text-xs text-muted-foreground">{t('shops.weekly')}</p>
                </div>
                <div>
                  <p className="text-lg font-bold">{shop.metrics.monthlyViews}</p>
                  <p className="text-xs text-muted-foreground">{t('shops.monthly')}</p>
                </div>
              </div>
              {shop.metrics.uniqueVisitors > 0 && (
                <>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">{t('shops.uniqueVisitors')}</span>
                    <span className="font-bold">{shop.metrics.uniqueVisitors}</span>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Contact */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Phone className="size-5" />
              {t('shops.contact')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {formatAddress(shop.address) && (
              <div className="flex items-start gap-2">
                <MapPin className="size-4 text-muted-foreground mt-1 shrink-0" />
                <span className="text-sm">{formatAddress(shop.address)}</span>
              </div>
            )}
            {isAddressObject(shop.address) && shop.address.phone_number && (
              <div className="flex items-center gap-2">
                <Phone className="size-4 text-muted-foreground" />
                <a href={`tel:${shop.address.phone_number}`} className="text-sm hover:underline">
                  {shop.address.phone_number}
                </a>
              </div>
            )}
            {(shop.phone || shop.phoneNumber) && (
              <div className="flex items-center gap-2">
                <Phone className="size-4 text-muted-foreground" />
                <a href={`tel:${shop.phone || shop.phoneNumber}`} className="text-sm hover:underline">
                  {shop.phone || shop.phoneNumber}
                </a>
              </div>
            )}
            {shop.email && (
              <div className="flex items-center gap-2">
                <Mail className="size-4 text-muted-foreground" />
                <a href={`mailto:${shop.email}`} className="text-sm hover:underline">
                  {shop.email}
                </a>
              </div>
            )}
            {(getCountryName(shop.country) || (isAddressObject(shop.address) && getCountryName(shop.address.country))) && (
              <div className="flex items-center gap-2">
                <Globe className="size-4 text-muted-foreground" />
                <span className="text-sm">
                  {isCountryObject(shop.country) && shop.country.flagEmoji && (
                    <span className="mr-1">{shop.country.flagEmoji}</span>
                  )}
                  {getCountryName(shop.country) || (isAddressObject(shop.address) ? getCountryName(shop.address.country) : '')}
                </span>
              </div>
            )}
            {(shop.created_at || shop.createdAt) && (
              <div className="flex items-center gap-2">
                <Calendar className="size-4" />
                <span>{t('common.createdAt')}: </span>
                <span className="text-foreground">{formatDate(shop.created_at || shop.createdAt)}</span>
              </div>
            )}
             {shop.updatedAt && (
              <div className="flex items-center gap-2">
                <Clock className="size-4" />
                <span>{t('common.updatedAt')}: </span>
                <span className="text-foreground">{formatDate(shop.updatedAt)}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Open Hours */}
        {hasOpenHours && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="size-5" />
                {t('shops.openHours')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                <DaySchedule day="monday" hours={shop.monday} t={t} />
                <DaySchedule day="tuesday" hours={shop.tuesday} t={t} />
                <DaySchedule day="wednesday" hours={shop.wednesday} t={t} />
                <DaySchedule day="thursday" hours={shop.thursday} t={t} />
                <DaySchedule day="friday" hours={shop.friday} t={t} />
                <DaySchedule day="saturday" hours={shop.saturday} t={t} />
                <DaySchedule day="sunday" hours={shop.sunday} t={t} />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Description */}
        {shop.description && (
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>{t('shops.description')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground whitespace-pre-wrap">{shop.description}</p>
            </CardContent>
          </Card>
        )}

        {/* Disable Reason */}
        {shop.disableCommentReason && (
          <Card className="border-destructive">
            <CardHeader>
              <CardTitle className="text-destructive">{t('shops.disableReason')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">{shop.disableCommentReason}</p>
            </CardContent>
          </Card>
        )}

        {/* Shipping Rates */}
        {Array.isArray(shop.shippingRates) && shop.shippingRates.length > 0 && (
          <Card className="md:col-span-2 lg:col-span-3">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="size-5" />
                {t('shops.shippingRates')} ({shop.shippingRates.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {shop.shippingRates.map((rate: ShopShippingRate) => (
                  <div
                    key={rate.refId}
                    className={`p-4 rounded-lg border ${rate.isActive ? 'bg-card' : 'bg-muted/50 opacity-60'}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-medium">{rate.name}</h4>
                        {rate.description && (
                          <p className="text-xs text-muted-foreground line-clamp-2">{rate.description}</p>
                        )}
                      </div>
                      <Badge variant={rate.isActive ? 'default' : 'secondary'}>
                        {rate.isActive ? t('common.active') : t('common.inactive')}
                      </Badge>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">{t('shops.shippingPrice')}</span>
                        <span className="font-bold text-primary">
                          {rate.prices ? formatPrice(getAmountFromPriceObject(rate.prices, currency), currency) : formatPrice(rate.price, rate.currency || currency)}
                        </span>
                      </div>
                      {rate.freeShippingThreshold && parseFloat(rate.freeShippingThreshold) > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">{t('shops.freeShippingFrom')}</span>
                          <span className="text-green-600">{formatPrice(rate.freeShippingThreshold, rate.currency || currency)}</span>
                        </div>
                      )}
                      {(rate.estimatedDeliveryDays || rate.maxDeliveryDays) && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">{t('shops.deliveryTime')}</span>
                          <span>
                            {rate.estimatedDeliveryDays && rate.maxDeliveryDays
                              ? `${rate.estimatedDeliveryDays}-${rate.maxDeliveryDays} ${t('shops.daysUnit')}`
                              : `${rate.estimatedDeliveryDays || rate.maxDeliveryDays} ${t('shops.daysUnit')}`}
                          </span>
                        </div>
                      )}
                      {rate.deliveryType && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">{t('shops.deliveryTypeLabel')}</span>
                          <Badge variant="outline" className="text-xs">
                            {t(`shops.shippingTypes.${rate.deliveryType}`)}
                          </Badge>
                        </div>
                      )}
                      {rate.availableCountries && rate.availableCountries.length > 0 && (
                        <div className="pt-2 border-t">
                          <p className="text-xs text-muted-foreground mb-1">{t('shops.availableCountries')}</p>
                          <div className="flex flex-wrap gap-1">
                            {rate.availableCountries.slice(0, 4).map((countryCode) => {
                              const flag = getCountryFlagByCode(countryCode);
                              const name = getCountryNameByCode(countryCode);
                              return (
                                <Badge key={countryCode} variant="secondary" className="text-xs gap-1">
                                  {flag && <span>{flag}</span>}
                                  <span>{name}</span>
                                </Badge>
                              );
                            })}
                            {rate.availableCountries.length > 4 && (
                              <Badge variant="outline" className="text-xs">
                                +{rate.availableCountries.length - 4}
                              </Badge>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Promotions */}
        {Array.isArray(shop.promotions) && shop.promotions.length > 0 && (
          <Card className="md:col-span-2 lg:col-span-3">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Tag className="size-5" />
                {t('shops.promotionsActive')} ({shop.promotions.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {shop.promotions.map((promo: ShopPromotionSummary) => {
                  const isActive = promo.status === 'active';
                  const isPaused = promo.status === 'paused';
                  const StatusIcon = isActive ? Play : isPaused ? Pause : XCircle;
                  const TypeIcon = promo.type === 'percentage' ? Percent : Tag;
                  const promoValue = parseFloat(promo.value) || 0;

                  return (
                    <Link
                      key={promo.refId}
                      to={`/promotions/${promo.refId}`}
                      className="block"
                    >
                      <div
                        className={`p-4 rounded-lg border hover:border-primary/50 transition-colors ${
                          isActive ? 'bg-card' : 'bg-muted/50'
                        }`}
                        style={{ borderLeftColor: promo.badgeColor, borderLeftWidth: 4 }}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div
                              className="p-1.5 rounded"
                              style={{ backgroundColor: promo.badgeColor ? `${promo.badgeColor}20` : undefined }}
                            >
                              <TypeIcon
                                className="size-4"
                                style={{ color: promo.badgeColor || undefined }}
                              />
                            </div>
                            <div>
                              <h4 className="font-medium">{promo.displayName || promo.name}</h4>
                              {promo.displayName && promo.displayName !== promo.name && (
                                <p className="text-xs text-muted-foreground">{promo.name}</p>
                              )}
                            </div>
                          </div>
                          <Badge
                            variant={isActive ? 'default' : isPaused ? 'secondary' : 'destructive'}
                            className="gap-1"
                          >
                            <StatusIcon className="size-3" />
                            {t(`promotions.status.${promo.status}`)}
                          </Badge>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">{t('promotions.value')}</span>
                            <span
                              className="font-bold text-lg"
                              style={{ color: promo.badgeColor || undefined }}
                            >
                              {promo.type === 'percentage' ? `${promoValue}%` : formatPrice(convertFromEur(promoValue, currency), currency)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-muted-foreground">{t('promotions.period')}</span>
                            <span>
                              {formatDate(promo.startDate, 'dd/MM')} - {formatDate(promo.endDate, 'dd/MM/yyyy')}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-muted-foreground">{t('promotions.usage')}</span>
                            <span>
                              {promo.currentUsage}
                              {promo.maxUsage ? ` / ${promo.maxUsage}` : ` (${t('promotions.unlimited')})`}
                            </span>
                          </div>
                          {promo.productsCount > 0 && (
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-muted-foreground">{t('promotions.products')}</span>
                              <span>{promo.productsCount}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Timestamps */}
      {/* <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-6 text-sm text-muted-foreground">
            {(shop.created_at || shop.createdAt) && (
              <div className="flex items-center gap-2">
                <Calendar className="size-4" />
                <span>{t('common.createdAt')}: </span>
                <span className="text-foreground">{formatDate(shop.created_at || shop.createdAt)}</span>
              </div>
            )}
            {shop.updatedAt && (
              <div className="flex items-center gap-2">
                <Clock className="size-4" />
                <span>{t('common.updatedAt')}: </span>
                <span className="text-foreground">{formatDate(shop.updatedAt)}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card> */}
    </div>
  );
}
